/** Sonic-boom bow-shock wavefronts at the fist position during the active phase of a Punchmore attack. */
import Phaser from 'phaser';
import { Component } from './Component';
import { CRISP_SCALE } from '../ui/CrispUtil';
import { ConfigLoader } from '../systems/ConfigLoader';
import type { AttackState } from './CombatComponent';
import { getFistPosition } from '../utils/PlayerGeometry';
import type { Position } from '../utils/Types';

interface WaveConfig {
  centerOffset: number;
  radius: number;
  halfSweep: number;
  alpha: number;
}

interface RadialOffset {
  dr: number;
  alphaScale: number;
}

interface ImpactConfig {
  peakAlpha: number;
  fadeMs: number;
  canvasHalf: number;
  coreLineWidth: number;
  waves: WaveConfig[];
  radialOffsets: RadialOffset[];
}

const INV_CRISP = 1 / CRISP_SCALE;
const TEX_KEY = '_punchmore-impact';

/**
 * Each wavefront is a forward-bowing arc whose center is displaced BACKWARD from the fist.
 * The arc radius ≈ center offset so the arc's forward tip grazes the fist — exactly a Mach cone.
 * Inner wavefronts are tighter and brighter; outer ones spread wider and dimmer.
 *
 * All tuning lives in punchmore.json under the "impact" key.
 */
export class PunchmoreImpactComponent implements Component {
  private scene: Phaser.Scene;
  private sprite: Phaser.GameObjects.Sprite;
  private facing: Position;

  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private image: Phaser.GameObjects.Image;

  private cfg: ImpactConfig;
  private canvasPx: number;

  private currentAlpha = 0;
  private targetAlpha = 0;

  constructor(
    scene: Phaser.Scene,
    sprite: Phaser.GameObjects.Sprite,
    events: Phaser.Events.EventEmitter,
    facing: Position,
  ) {
    this.scene = scene;
    this.sprite = sprite;
    this.facing = facing;

    const raw = ConfigLoader.getAttackConfig('punchmore') as Record<string, unknown> | undefined;
    this.cfg = (raw?.['impact'] as ImpactConfig | undefined) ?? {
      peakAlpha: 0.35,
      fadeMs: 120,
      canvasHalf: 64,
      coreLineWidth: 1.5,
      waves: [
        { centerOffset: 6,  radius: 8,  halfSweep: 0.90, alpha: 1.0  },
        { centerOffset: 14, radius: 16, halfSweep: 1.05, alpha: 0.75 },
        { centerOffset: 26, radius: 28, halfSweep: 1.20, alpha: 0.45 },
      ],
      radialOffsets: [
        { dr: -3, alphaScale: 0.12 }, { dr: -2, alphaScale: 0.25 },
        { dr: -1, alphaScale: 0.55 }, { dr:  0, alphaScale: 1.0  },
        { dr:  1, alphaScale: 0.55 }, { dr:  2, alphaScale: 0.25 },
        { dr:  3, alphaScale: 0.12 },
      ],
    };

    this.canvasPx = this.cfg.canvasHalf * 2 * CRISP_SCALE;

    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvasPx;
    this.canvas.height = this.canvasPx;
    this.ctx = this.canvas.getContext('2d')!;

    if (!scene.textures.exists(TEX_KEY)) {
      scene.textures.addCanvas(TEX_KEY, this.canvas);
      scene.textures.get(TEX_KEY).setFilter(Phaser.Textures.FilterMode.LINEAR);
    }

    this.image = scene.add.image(0, 0, TEX_KEY)
      .setScale(INV_CRISP)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);

    events.on('attack-state-changed', this.onAttackStateChanged, this);
  }

  private onAttackStateChanged(state: AttackState, attackType: string): void {
    if (attackType !== 'punchmore') {
      this.targetAlpha = 0;
      return;
    }
    this.targetAlpha = state === 'active' ? this.cfg.peakAlpha : 0;
  }

  update(delta: number): void {
    if (this.targetAlpha > 0) {
      this.currentAlpha = this.targetAlpha;
    } else if (this.currentAlpha > 0) {
      this.currentAlpha = Math.max(0, this.currentAlpha - this.cfg.peakAlpha * (delta / this.cfg.fadeMs));
    }

    if (this.currentAlpha <= 0.01) {
      this.image.setVisible(false);
      this.currentAlpha = 0;
      return;
    }

    const { x: cx, y: cy, nx, ny } = getFistPosition(this.sprite.x, this.sprite.y, this.facing);

    this.drawBowShock(nx, ny);
    (this.scene.textures.get(TEX_KEY) as Phaser.Textures.CanvasTexture).refresh();

    this.image.setPosition(cx, cy);
    this.image.setAlpha(this.currentAlpha);
    this.image.setDepth(this.sprite.depth + 1);
    this.image.setVisible(true);
  }

  /**
   * Each arc's center is displaced backward from the canvas center (fist position).
   * The arc sweeps around the forward direction, bowing out in front of the fist.
   */
  private drawBowShock(nx: number, ny: number): void {
    const S = CRISP_SCALE;
    const center = this.cfg.canvasHalf * S;
    const forwardAngle = Math.atan2(ny, nx);
    const backAngle = forwardAngle + Math.PI;

    this.ctx.clearRect(0, 0, this.canvasPx, this.canvasPx);
    this.ctx.lineCap = 'round';

    for (const wave of this.cfg.waves) {
      const arcCx = center + Math.cos(backAngle) * wave.centerOffset * S;
      const arcCy = center + Math.sin(backAngle) * wave.centerOffset * S;

      for (const offset of this.cfg.radialOffsets) {
        this.drawArcGradient(
          arcCx, arcCy,
          (wave.radius + offset.dr) * S,
          forwardAngle - wave.halfSweep,
          forwardAngle + wave.halfSweep,
          this.cfg.coreLineWidth * S,
          wave.alpha * offset.alphaScale,
        );
      }
    }
  }

  /** Draws an arc in segments with a sin-bell alpha falloff — bright at center, fading at both tips. */
  private drawArcGradient(
    cx: number, cy: number, radius: number,
    startAngle: number, endAngle: number,
    lineWidth: number, peakAlpha: number,
  ): void {
    const SEGMENTS = 20;
    const span = endAngle - startAngle;
    this.ctx.lineWidth = lineWidth;
    this.ctx.lineCap = 'butt';

    for (let i = 0; i < SEGMENTS; i++) {
      const tMid = (i + 0.5) / SEGMENTS;
      const brightness = Math.sin(tMid * Math.PI); // 0 at tips, 1 at center
      this.ctx.strokeStyle = `rgba(255, 255, 255, ${peakAlpha * brightness})`;
      this.ctx.beginPath();
      this.ctx.arc(cx, cy, radius, startAngle + (i / SEGMENTS) * span, startAngle + ((i + 1) / SEGMENTS) * span, false);
      this.ctx.stroke();
    }

    this.ctx.lineCap = 'round';
  }

  destroy(): void {
    this.image.destroy();
    if (this.scene.textures.exists(TEX_KEY)) {
      this.scene.textures.remove(TEX_KEY);
    }
  }
}
